/**
 * MODAL: EmailTemplateModal
 * Création / édition d'un modèle d'email réutilisable (item #7 retours client).
 * Utilisé depuis la page admin /admin/email-templates.
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailTemplateSchema, type EmailTemplateInput } from '@/schemas/emailTemplate';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, RichTextEditor } from '../ui';
import type { EmailTemplate } from '@/types/emailTemplate';
import { ALL_TAG_CATEGORIES } from '@/lib/emailTags';
import { cn } from '@/lib/utils';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Si fourni, mode édition. Sinon mode création. */
  template?: EmailTemplate | null;
  onSubmit: (data: EmailTemplateInput) => Promise<void>;
}

export function EmailTemplateModal({
  isOpen,
  onClose,
  template,
  onSubmit,
}: EmailTemplateModalProps) {
  const form = useForm<EmailTemplateInput>({
    resolver: zodResolver(emailTemplateSchema),
    defaultValues: {
      name: '',
      subject: '',
      body: '',
      entity_scope: 'all',
    },
  });

  // Recharger les valeurs à chaque ouverture (édition vs création)
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: template?.name ?? '',
        subject: template?.subject ?? '',
        body: template?.body ?? '',
        entity_scope: template?.entity_scope ?? 'all',
      });
    }
  }, [isOpen, template, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: EmailTemplateInput) => {
    await onSubmit(data);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={template ? 'Modifier le modèle' : "Créer un modèle d'email"}
      size="2xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel={template ? 'Enregistrer' : 'Créer'}
          isSubmitting={form.formState.isSubmitting}
          formId="email-template-form"
        />
      }
    >
      <Form {...form}>
        <form id="email-template-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control as any}
              name="name"
              label="Nom du modèle"
              required
              placeholder="Ex: Relance BED incomplet"
            />

            {/* Visibilité du template */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Visible depuis</label>
              <Controller
                name="entity_scope"
                control={form.control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    {(
                      [
                        { value: 'all', label: 'Toutes les fiches' },
                        { value: 'crew', label: 'Fiche équipage' },
                        { value: 'contact', label: 'Fiche contact' },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => field.onChange(opt.value)}
                        className={cn(
                          'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                          field.value === opt.value
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              />
            </div>

            <FormInput
              control={form.control as any}
              name="subject"
              label="Sujet de l'email"
              required
              placeholder="Ex: Votre dossier 4L Trophy — il manque quelques infos"
            />

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Corps du message <span className="text-red-500">*</span>
              </label>
              <Controller
                name="body"
                control={form.control}
                render={({ field, fieldState }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Rédigez le corps du modèle..."
                    minHeight="280px"
                    error={fieldState.error?.message}
                    tagCategories={ALL_TAG_CATEGORIES}
                  />
                )}
              />
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
